'use client';

import { motion } from 'framer-motion';

/**
 * RestoringLoader Component
 * Displays a loading animation while restoring a session from cloud
 */
const RestoringLoader = () => {
  // Shimmer animation for the loader
  const shimmerAnimation = {
    animate: {
      backgroundPosition: ['200% 0', '-200% 0'],
    },
    transition: {
      duration: 1.5,
      ease: 'linear',
      repeat: Infinity,
    },
  };

  // Pulse animation for the text
  const pulseAnimation = {
    animate: {
      opacity: [0.5, 1, 0.5],
    },
    transition: {
      duration: 2,
      ease: 'easeInOut',
      repeat: Infinity,
    },
  };

  return (
    <div className="min-h-screen bg-[#F9F6EB] flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {/* Book Icon with shimmer */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="mb-8 flex justify-center"
        >
          <div className="relative w-20 h-20">
            <motion.div
              animate={shimmerAnimation.animate}
              transition={shimmerAnimation.transition}
              className="absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-[#E8DFC3] to-transparent"
              style={{
                backgroundImage: 'linear-gradient(90deg, transparent, rgba(232,223,195,0.6), transparent)',
                backgroundSize: '200% 100%',
              }}
            />
            <svg
              className="w-full h-full text-[#8B7355]"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 19.5C4 18.837 4.26339 18.2011 4.73223 17.7322C5.20107 17.2634 5.83696 17 6.5 17H20"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.5 2H20V22H6.5C5.83696 22 5.20107 21.7366 4.73223 21.2678C4.26339 20.7989 4 20.163 4 19.5V4.5C4 3.83696 4.26339 3.20107 4.73223 2.73223C5.20107 2.26339 5.83696 2 6.5 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </motion.div>

        {/* Loading Text */}
        <motion.div
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
        >
          <motion.h2
            animate={pulseAnimation.animate}
            transition={pulseAnimation.transition}
            className="text-2xl md:text-3xl font-semibold text-[#5A4A3A] mb-3"
          >
            Restoring your book...
          </motion.h2>
          <p className="text-[#8B7355] text-sm md:text-base">
            Please wait while we load your saved progress
          </p>
        </motion.div>

        {/* Animated dots */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex justify-center gap-2 mt-8"
        >
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.2,
                repeat: Infinity,
                delay: i * 0.2,
                ease: 'easeInOut',
              }}
              className="w-2 h-2 bg-[#8B7355] rounded-full"
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default RestoringLoader;
